import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { SessionCtx, useSessionCtx } from "../../context/SessionContext.jsx";
import { useUserPollActiveIds } from "../../hooks/useAdminActiveHistorySync.js";
import { isAdminChatUserId } from "../../utils/chatIdentity.js";
import useSessionState from "../../hooks/useSessionState.js";
import AdminLayout from "../../layouts/AdminLayout.jsx";
import { SessionGate } from "../../layouts/SessionGate.jsx";
import MeetingPanel from "../../meeting/MeetingPanel.jsx";
import Dashboard from "../DashboardPage/DashboardPage.jsx";
import { HLSPlayer } from "../../components/lazyPlayers.jsx";
import Poll from "../PollPage/PollPage.jsx";
import Qna from "../QnaPage/QnaPage.jsx";
import InteractiveQuiz from "../InteractiveQuizPage/InteractiveQuizPage.jsx";
import Chat from "../ChatPage/ChatPage.jsx";
import Sectoin from "../SectionPage/SectionPage.jsx";
import { FullViewModal } from "../../components/FullViewModal.jsx";
import { CreativeSelector } from "../../components/CreativeSelector.jsx";
import RealtimeDownModal from "../../components/RealtimeDownModal.jsx";
import Toaster from "../../components/Toaster.jsx";
import useCreativeSrc from "../../hooks/useCreativeSrc.js";
import useStreamPlayback, {
  playbackReasonText,
} from "../../hooks/useStreamPlayback.js";
import { streamingUrlOf } from "../../utils/streamingUrl.js";
import useLinkPlayable from "../../hooks/useLinkPlayable.js";
import { linkCheckReasonText } from "../../utils/linkPlayable.js";
import { isWebPageVendor } from "../../utils/webPageStream.js";
import useTotalUsers from "../../hooks/useTotalUsers.js";
import useViewerCount from "../../hooks/useViewerCount.js";
import useHcpUsers from "../../hooks/useHcpUsers.js";
import { ViewersModal } from "./ViewersModal.jsx";
import useMediaQuery from "../../hooks/useMediaQuery.js";
import useFavicon from "../../hooks/useFavicon.js";
import useDocumentTitle from "../../hooks/useDocumentTitle.js";
import { MOBILE_STEPS_SLOT_ID } from "../DashboardPage/StreamingVendorControls.jsx";
import { deriveStreamHealth } from "../../utils/streamHealth.js";
import { openViewerTab } from "../../utils/viewerLink.js";
import useOnlineStatus from "../../hooks/useOnlineStatus.js";
import useConnectionToasts from "../../hooks/useConnectionToasts.js";
import useNetworkSpeed from "../../hooks/useNetworkSpeed.js";
import { speedQuality } from "../../utils/networkSpeed.js";

// Viewer stream (the HLS link viewers watch), shown inline instead of in a popup.
// Same card style as the Dashboard panels (white, rounded-2xl, dark gradient icon badge).
// Compact: it sits in the narrow 15% left column. h-full + flex-1 video make it share
// the exact row height with the other cards in the 3-column presentation grid.
// Clicking the card (video included) opens the HCP viewer page for this session in a new
// tab, logged in as the .env test HCP (utils/viewerLink.js); the expand button opens the
// same player in a modal instead.
// `creative` is on while a stratcreative overlay is live (that src wins over the
// cme-common stream url, exactly like the viewer's video slot).
function LiveStreamCard({ src, creative = false, className = "" }) {
  const ctx = useSessionCtx();
  const sessionId = ctx?.session?.sessionId;
  const showMsg = ctx?.showMsg;
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  // Live viewer count (Ably presence), shown in the full-view modal's header.
  const totalUsers = useTotalUsers();
  // Clicking the card only asks first; the viewer login API runs on "Yes" alone.
  const [confirmingViewer, setConfirmingViewer] = useState(false);
  const cancelViewer = useCallback(() => setConfirmingViewer(false), []);
  // True while the viewer login is in flight; repeat clicks are ignored meanwhile.
  const [openingViewer, setOpeningViewer] = useState(false);
  const askOpenViewer = () => {
    if (openingViewer || sessionId == null) return;
    setConfirmingViewer(true);
  };
  const openViewer = async () => {
    setConfirmingViewer(false);
    if (openingViewer || sessionId == null) return;
    setOpeningViewer(true);
    try {
      await openViewerTab(sessionId);
    } catch (e) {
      showMsg?.(e?.message || "Couldn't open the viewer page", "error");
    } finally {
      setOpeningViewer(false);
    }
  };

  return (
    <>
      <section
        onClick={askOpenViewer}
        title="Open the viewer page in a new tab"
        aria-busy={openingViewer}
        className={`bg-white rounded-2xl border border-gray-200/70 p-2.5 h-full flex flex-col cursor-pointer hover:shadow-sm hover:border-gray-300 transition ${className}`}
      >
        <header className="flex items-center gap-2 mb-2">
          <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-gray-800 to-black text-white flex items-center justify-center shrink-0">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </span>
          {!creative && (
            <h3 className="text-xs font-semibold text-gray-800 truncate">
              Live Stream
            </h3>
          )}
          {creative && (
            <span className="text-[9px] font-bold uppercase tracking-wide text-white bg-emerald-600 px-1.5 py-0.5 rounded">
              Creative
            </span>
          )}
          {openingViewer && (
            <span className="ml-auto text-[9px] font-medium text-gray-500 shrink-0">
              Opening…
            </span>
          )}
        </header>
        {/* Whole card (video included) opens the viewer tab; the expand button opens the
            modal, whose player has the controls. */}
        <div className="relative aspect-video w-full min-h-0 max-h-[120px] flex-1 rounded-lg overflow-hidden bg-black">
          {src ? (
            open ? null : (
              <HLSPlayer
                src={src}
                muted={true}
                autoPlay={true}
                showControls={false}
                compact
              />
            )
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-gray-100 text-gray-400 px-1 text-center">
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 3l18 18"
                />
              </svg>
              <span className="text-[11px] leading-tight">
                No streaming link yet
              </span>
            </div>
          )}
          <button
            type="button"
            title="Open live stream"
            aria-label="Open live stream in a larger player"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md bg-black/55 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4"
              />
            </svg>
          </button>
        </div>
      </section>

      {open && (
        <FullViewModal
          labelId="live-stream-modal-title"
          onClose={close}
          meta={
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Viewer stream
            </span>
          }
          title={
            <span className="text-base font-semibold text-gray-900">
              Live Stream
            </span>
          }
          trailing={
            <span
              className="inline-flex items-center gap-1 self-start mt-0.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 shrink-0 tabular-nums"
              title="Viewers currently on this session"
            >
              <span className="text-[10px] font-medium text-emerald-600">
                Viewers
              </span>
              <span className="text-[11px] font-bold text-emerald-700">
                {totalUsers}
              </span>
            </span>
          }
        >
          <div className="aspect-video w-full rounded-xl overflow-hidden bg-black">
            {src ? (
              <HLSPlayer
                src={src}
                muted={true}
                autoPlay={true}
                showControls={true}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-sm px-4 text-center">
                No streaming link yet
              </div>
            )}
          </div>
        </FullViewModal>
      )}

      {confirmingViewer && (
        <OpenViewerConfirm onYes={openViewer} onNo={cancelViewer} />
      )}
    </>
  );
}

// "Open the user view?" Yes / No, asked before the viewer login API is called.
// No, Escape and a backdrop click all just close it.
function OpenViewerConfirm({ onYes, onNo }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onNo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNo]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onNo}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="open-viewer-title"
        aria-describedby="open-viewer-message"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xs rounded-2xl border border-gray-200 bg-white p-5 text-center shadow-xl"
      >
        <p id="open-viewer-title" className="text-sm font-semibold text-gray-800">
          Open the user view?
        </p>
        <p id="open-viewer-message" className="mt-1 text-xs text-gray-500">
          The viewer page opens in a new tab.
        </p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onNo}
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
          >
            No
          </button>
          <button
            type="button"
            autoFocus
            onClick={onYes}
            className="flex-1 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
          >
            Yes
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Card-style button that opens the Settings (component visibility) controls in a modal.
// Compact to fit the narrow left column; same look as the Live Stream / Stream Health cards.
function SectionCard({ className = "" }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`w-full flex-1 flex items-center gap-2 bg-white rounded-2xl border border-gray-200/70 p-3 text-left hover:shadow-sm hover:border-gray-300 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 ${className}`}
      >
        <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-gray-800 to-black text-white flex items-center justify-center shrink-0">
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z" />
          </svg>
        </span>
        <span className="text-xs font-semibold text-gray-800 truncate">
          Settings
        </span>
        <svg
          className="w-4 h-4 ml-auto text-gray-400 shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {open && (
        <FullViewModal
          labelId="settings-modal-title"
          onClose={close}
          closeOnBackdrop={false}
          title={
            <span className="text-base font-semibold text-gray-900">
              Settings
            </span>
          }
        >
          <Sectoin embedded />
        </FullViewModal>
      )}
    </>
  );
}

// "Running" pill: a creative is live on the stratcreative channel.
function RunningPill({ className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white shadow-sm ${className}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" aria-hidden="true" />
      Running
    </span>
  );
}

// Card-style button (after Settings) that opens the Creative Videos picker in a modal - the
// same CreativeSelector the Dashboard used to show, its Stop Creative button moved to the
// footer's left side (stopTarget). running = a creative is live
// (useCreativeSrc, the same stratcreative state the picker reads): a Running pill sits
// under the "Creative" label.
function CreativeCard({ running = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  // The footer's left side, where the picker puts its Stop Creative button (Cancel stays right).
  const [stopSlot, setStopSlot] = useState(null);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`w-full flex-1 flex items-center gap-2 bg-white rounded-2xl border border-gray-200/70 p-3 text-left hover:shadow-sm hover:border-gray-300 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 ${className}`}
      >
        <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-gray-800 to-black text-white flex items-center justify-center shrink-0">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 10l4.55-2.28A1 1 0 0121 8.62v6.76a1 1 0 01-1.45.9L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </span>
        <span className="min-w-0 flex flex-col items-start gap-1">
          <span className="text-xs font-semibold text-gray-800 truncate">
            Creative
          </span>
          {running && <RunningPill />}
        </span>
        <svg
          className="w-4 h-4 ml-auto text-gray-400 shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {open && (
        <FullViewModal
          labelId="creative-modal-title"
          onClose={close}
          closeOnBackdrop={false}
          title={
            <span className="text-base font-semibold text-gray-900">
              Creative Videos
            </span>
          }
          subtitle={
            <p className="text-xs text-gray-500">
              Show a video to every viewer in place of the live stream.
            </p>
          }
          footer={
            <>
              <div ref={setStopSlot} className="mr-auto flex" />
              <button
                type="button"
                onClick={close}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
              >
                Cancel
              </button>
            </>
          }
        >
          <CreativeSelector stopTarget={stopSlot} />
        </FullViewModal>
      )}
    </>
  );
}

// Stream health for the viewer stream (moved here from the AdminLayout header).
// Compact to fit the narrow left column. Greyed out until a streaming URL exists.
// Two legs of the pipeline, each measured from where it is actually observable:
//   Started  - session.data.health.is_live: the vendor feed reaches Mux over RTMP
//   Playing  - probed every 30s from the manifest behind the stream url plus a 1kB
//              segment fetch (useStreamPlayback): advancing + bytes served = confirmed
// Nothing is invented here - until the first health tick lands, Started shows Waiting;
// Playing is measured either way, so it shows from the start.
// (deriveStreamHealth itself is shared with the Go Live gate: utils/streamHealth.js)
// webPage (streaming vendor "Web Page"): the stream is a link the admin typed in - no Mux,
// so no Started / health; only Playing, from the link check (useLinkPlayable).

// One Yes/No line: green pulsing dot when true, flat grey dot when false, grey "Checking"
// (or pendingText) while the answer is still being measured.
function HealthFlag({ label, on, pending = false, pendingText = "Checking" }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700">
      <span className="relative flex h-2 w-2 shrink-0">
        {on && !pending && (
          <span
            className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
            aria-hidden="true"
          />
        )}
        <span
          className={`relative inline-flex rounded-full h-2 w-2 ${on && !pending ? "bg-emerald-500" : "bg-slate-300"}`}
          aria-hidden="true"
        />
      </span>
      <span className="text-slate-500 font-medium">{label}</span>
      <span className={on && !pending ? "text-emerald-600" : "text-slate-400"}>
        {pending ? pendingText : on ? "Yes" : "No"}
      </span>
    </div>
  );
}

function StreamHealthCard({
  src,
  health = null,
  playback = { playing: null, reason: "no-url", confirmedAt: null },
  webPage = false,
  className = "",
}) {
  const info = deriveStreamHealth(health);
  const reasonText = webPage ? linkCheckReasonText : playbackReasonText;

  return (
    <section
      className={`bg-white rounded-2xl border border-gray-200/70 p-3 flex-1 ${className}`}
      aria-disabled={!src}
    >
      <header className="flex items-center gap-2 mb-2.5">
        <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-gray-800 to-black text-white flex items-center justify-center shrink-0">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        </span>
        <h3 className="text-xs font-semibold text-gray-800 truncate">
          Stream Health
        </h3>
      </header>
      <div
        className={`rounded-lg bg-gray-50 px-2.5 py-2 ${src ? "" : "opacity-60"}`}
        title={src ? "Stream Health" : "No streaming link yet"}
      >
        {!src ? (
          <div className="flex items-center gap-1.5 text-slate-500">
            <span className="h-2 w-2 rounded-full bg-slate-300 shrink-0" />
            <span className="text-[10px] font-bold tracking-wide uppercase">
              No stream
            </span>
          </div>
        ) : (
          <div className="space-y-1">
            {!webPage && (
              <HealthFlag label="Started" on={info?.started === true} pending={!info} pendingText="Waiting" />
            )}
            <HealthFlag
              label="Playing"
              on={playback.playing === true}
              pending={playback.playing == null}
            />
            {/* Confirmed every 30s: playlist advancing + real segment bytes served. */}
            {playback.playing === true && (
              <p className="text-[9px] leading-tight font-semibold text-emerald-600">
                Streaming Checked
                {playback.confirmedAt
                  ? ` · ${new Date(playback.confirmedAt).toLocaleTimeString()}`
                  : ""}
              </p>
            )}
            {playback.playing === false && (
              <p
                className="text-[9px] leading-tight font-semibold text-rose-500"
                title={reasonText(playback.reason)}
              >
                {reasonText(playback.reason)}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// Live viewer count (useViewerCount: Ably channel occupancy, re-read every 15s).
// A dash until the first read succeeds. A click opens the HCP viewers on the users channel
// (useHcpUsers - followed from mount, so the list is ready) with Join / Remove per viewer.
function ViewersCard({ className = "" }) {
  const ctx = useSessionCtx();
  const sessionId = ctx?.session?.sessionId;
  const count = useViewerCount(sessionId);
  const { users, setJoinMeeting } = useHcpUsers(sessionId);
  const [open, setOpen] = useState(false);
  return (
    <section
      className={`group relative bg-white rounded-xl border border-gray-200/70 px-3 py-2 flex items-center gap-2 transition hover:border-gray-300 hover:bg-gray-50/60 ${className}`}
    >
      <span className="w-6 h-6 rounded-md bg-gradient-to-br from-gray-800 to-black text-white flex items-center justify-center shrink-0">
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      </span>
      <h3 className="text-xs font-semibold text-gray-800 truncate">Viewers</h3>
      <p
        className="ml-auto text-sm font-semibold text-slate-800 tabular-nums"
        aria-label="Viewer count"
        title={count == null ? "Viewer count not available yet" : "Viewers watching right now"}
      >
        {count == null ? "—" : count.toLocaleString()}
      </p>
      {/* Right after the count (only the count is ml-auto - two would split the space and
          leave the count mid-card); nudges right on hover to show the card opens something. */}
      <svg
        className="-mr-1 w-4 h-4 shrink-0 text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-gray-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
      {/* The whole card is the button: laid over it, so the heading and count stay as they are. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Show viewers"
        aria-haspopup="dialog"
        className="absolute inset-0 rounded-xl cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900"
      />
      {open && (
        <ViewersModal
          users={users}
          viewerCount={count}
          hasMeetingVendor={Boolean(ctx?.session?.data?.hosting?.vendor)}
          onJoin={(clientID) => setJoinMeeting(clientID, true)}
          onRemove={(clientID) => setJoinMeeting(clientID, false)}
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
}

// The admin's own internet as a wifi icon - no numbers: green = good for running a live
// session (5+ Mbps), yellow = fair (1.5+), red = poor (below); a red crossed-out wifi while the
// browser is offline; gray while there's no reading yet (Firefox / Safari measure it from the
// live stream preview, so they need it playing). The speed itself comes from useNetworkSpeed.
// Same compact row as Viewers.
const WIFI_STATES = {
  good: { tone: "text-emerald-500", label: "Good connection" },
  fair: { tone: "text-yellow-500", label: "Fair connection" },
  poor: { tone: "text-red-500", label: "Poor connection" },
  offline: { tone: "text-red-500", label: "Offline" },
  unknown: { tone: "text-gray-300", label: "Checking the connection" },
};
const WIFI_PATH = "M5 12.55a11 11 0 0114.08 0M1.42 9a16 16 0 0121.16 0M8.53 16.11a6 6 0 016.95 0M12 20h.01";
const WIFI_OFF_PATH =
  "M1 1l22 22M16.72 11.06A10.94 10.94 0 0119 12.55M5 12.55a10.94 10.94 0 015.17-2.39M10.71 5.05A16 16 0 0122.58 9M1.42 9a15.91 15.91 0 014.7-2.88M8.53 16.11a6 6 0 016.95 0M12 20h.01";

function InternetSpeedCard({ className = "" }) {
  const { online, source, mbps } = useNetworkSpeed();
  const state = !online ? "offline" : (source != null && speedQuality(mbps)) || "unknown";
  const { tone, label } = WIFI_STATES[state];
  // Why it's gray, on hover: Firefox / Safari read the speed off the live stream preview.
  const hint =
    state === "unknown" && source == null
      ? "Checking the connection - in this browser it's measured from the live stream preview while it plays"
      : label;
  return (
    <section
      className={`bg-white rounded-xl border border-gray-200/70 px-3 py-2 flex items-center gap-2 ${className}`}
      aria-label="Internet speed"
    >
      <span className="w-6 h-6 rounded-md bg-gradient-to-br from-gray-800 to-black text-white flex items-center justify-center shrink-0">
        {/* Globe: the card is about the internet; the wifi icon on the right is its status. */}
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 2a10 10 0 100 20 10 10 0 000-20zM2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
        </svg>
      </span>
      <h3 className="text-xs font-semibold text-gray-800 truncate">Internet</h3>
      <svg
        className={`ml-auto w-5 h-5 shrink-0 transition-colors ${tone}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.25}
        strokeLinecap="round"
        strokeLinejoin="round"
        role="img"
        aria-label={label}
        data-state={state}
      >
        <title>{hint}</title>
        <path d={state === "offline" ? WIFI_OFF_PATH : WIFI_PATH} />
      </svg>
    </section>
  );
}

// Phone / tablet (< lg) version of StreamHealthCard: the same signals on one line,
// scrolling sideways if it does not fit. Sits right below the sticky header.
function StreamHealthStrip({
  src,
  health = null,
  playback = { playing: null, reason: "no-url", confirmedAt: null },
  webPage = false,
  className = "",
}) {
  const info = deriveStreamHealth(health);
  const reasonText = webPage ? linkCheckReasonText : playbackReasonText;

  return (
    <section
      className={`bg-white rounded-2xl border border-gray-200/70 px-3 py-2 flex items-center gap-3 overflow-x-auto whitespace-nowrap [scrollbar-width:none] ${className}`}
      aria-label="Stream health"
    >
      <h3 className="text-[11px] font-semibold text-gray-800 shrink-0">
        Stream Health
      </h3>
      {!src ? (
        <span className="flex items-center gap-1.5 text-slate-500 shrink-0">
          <span className="h-2 w-2 rounded-full bg-slate-300 shrink-0" />
          <span className="text-[10px] font-bold tracking-wide uppercase">No stream</span>
        </span>
      ) : (
        <>
          {!webPage && (
            <HealthFlag label="Started" on={info?.started === true} pending={!info} pendingText="Waiting" />
          )}
          <span
            className="shrink-0"
            title={
              playback.playing === false
                ? reasonText(playback.reason)
                : undefined
            }
          >
            <HealthFlag
              label="Playing"
              on={playback.playing === true}
              pending={playback.playing == null}
            />
          </span>
        </>
      )}
    </section>
  );
}

// Viewer stream URL, straight from the cme-common payload (session.data.streaming) - the
// same helper Go Live uses, so both probe one url and share one playing status.
const streamUrl = (session) => streamingUrlOf(session?.data?.streaming) || null;

// The left-column cards as one unit: in the left column normally, moved underneath the
// player while a quiz presentation is running (layout via className). Below lg only the
// live stream (creative) + Section cards show; MainPage puts them after the control tabs.
// Column/cell 1 = live stream, 2 = stream health + Viewers + Settings + Creative,
// 3 = children (the "Others" button, presentation only).
// creativeSrc = the live stratcreative overlay (wins over the cme-common stream url).
function StreamCards({
  src,
  creativeSrc = null,
  health = null,
  playback = null,
  webPage = false,
  className = "",
  children,
}) {
  return (
    <div className={className}>
      <LiveStreamCard
        src={creativeSrc || src}
        creative={Boolean(creativeSrc)}
      />
      <div className="flex flex-col gap-2 h-full">
        <StreamHealthCard
          src={src}
          health={health}
          playback={playback ?? undefined}
          webPage={webPage}
          className="hidden lg:block"
        />
        <ViewersCard />
        <SectionCard />
        <CreativeCard running={Boolean(creativeSrc)} />
        <InternetSpeedCard />
      </div>
      {children && <div className="flex flex-col gap-2 h-full">{children}</div>}
    </div>
  );
}

// Session control panels shown as tabs in the right column. Each renders the existing page in
// "embedded" mode (its own big SectionHeader hidden, since the tab is the title).
const CONTROL_PANELS = [
  {
    key: "poll",
    title: "Poll",
    icon: "M18 20V10 M12 20V4 M6 20V14",
    Body: Poll,
  },
  {
    key: "qna",
    title: "Q&A",
    icon: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    Body: Qna,
  },
  {
    key: "interactiveQuiz",
    title: "Quiz",
    icon: "M2 6h20v12H2z M12 12h.01 M17 12h.01 M7 12h.01",
    Body: InteractiveQuiz,
  },
  {
    key: "chat",
    title: "Chat",
    icon: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
    Body: Chat,
  },
];

// Radio filter shown in the summary line for the poll / quiz / Q&A tabs.
const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

// Same radio filter for the chat tab, over its own message states.
const CHAT_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "approved", label: "Approved" },
  { value: "admin", label: "Admin" },
  { value: "pinned", label: "Pinned" },
];

// Live numbers for each control area, from the same sources the panels use.
//   badge   = the TOTAL items in that area (always shown on the tab, capped at 99+)
//   live    = is any of it active for viewers (turns the badge emerald)
//   summary = the full breakdown on hover and in the open panel's summary line
function usePanelSummaries() {
  const ctx = useSessionCtx();
  // Poll: "active" comes from the user-poll channel, exactly like the Poll panel.
  const activePollIds = useUserPollActiveIds(ctx?.session?.sessionId);
  const polls = Array.isArray(ctx?.polls) ? ctx.polls : [];
  const pollActive = polls.filter((p) =>
    activePollIds.has(String(p.id)),
  ).length;

  const qna = Array.isArray(ctx?.qna) ? ctx.qna : [];
  const qnaActive = qna.filter((q) => q.isApproved).length;

  const quizzes = Array.isArray(ctx?.interactiveQuizzes)
    ? ctx.interactiveQuizzes
    : [];
  const quizActiveCount = (ctx?.activeInteractiveQuizzes || []).length;

  // Chat: viewer messages only (admin messages never need approval).
  const viewerMsgs = (ctx?.chatMessages || []).filter(
    (m) => !isAdminChatUserId(m.userId),
  );
  const chatApproved = viewerMsgs.filter((m) => m.isApproved).length;

  return {
    // badge = TOTAL content in that area (always shown); live = whether any of it is
    // active for viewers (emerald). The hover summary still breaks both down.
    poll: {
      badge: polls.length,
      live: pollActive > 0,
      summary: `${polls.length} total · ${pollActive} active`,
    },
    qna: {
      badge: qna.length,
      live: qnaActive > 0,
      summary: `${qna.length} total · ${qnaActive} active`,
    },
    interactiveQuiz: {
      badge: quizzes.length,
      live: quizActiveCount > 0,
      summary: `${quizzes.length} total · ${quizActiveCount} live`,
    },
    chat: {
      badge: viewerMsgs.length,
      live: chatApproved > 0,
      summary: `${viewerMsgs.length} messages · ${chatApproved} approved`,
    },
  };
}

// Count badge on a tab: always the same emerald pill - active/inactive and the selected
// tab do not change it (activity is still conveyed by the summary line's live dot).
function TabBadge({ badge }) {
  if (badge == null) {
    return (
      <span
        className="w-2 h-2 rounded-full bg-emerald-500"
        aria-hidden="true"
      />
    );
  }
  return (
    <span className="min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white text-[10px] font-bold tabular-nums">
      {badge > 99 ? "99+" : badge}
    </span>
  );
}

// Compact top tabs with count badges + a summary line for the open tab.
// Built for the narrow right column: the panel gets the full column width.
// One panel visible at a time (Poll by default); only the selected panel is mounted.
// Arrow Left/Right, Home, End move between tabs (WAI-ARIA tabs pattern).
// activeKey/onActiveKeyChange keep the selected tab while the tabs are unmounted
// (MainPage swaps them for the quiz presentation and swaps them back).
// fill: on lg+ the tabs fill their (relative) grid cell - as tall as the meeting player -
// instead of sizing to the panel's 60vh: the panel scrolls inside, chat stretches to it.
function ControlTabs({ className = "", activeKey, onActiveKeyChange, fill = false }) {
  const [internalKey, setInternalKey] = useState(CONTROL_PANELS[0].key);
  const selectedKey = activeKey ?? internalKey;
  const setActiveKey = onActiveKeyChange ?? setInternalKey;
  const stats = usePanelSummaries();
  const active =
    CONTROL_PANELS.find((p) => p.key === selectedKey) || CONTROL_PANELS[0];
  const ActiveBody = active.Body;
  // List filter: poll / quiz / Q&A share All / Active / Inactive; chat keeps its own
  // (All / Approved / Admin / Pinned) so switching tabs never mixes the two sets.
  const [filter, setFilter] = useState("all");
  const [chatFilter, setChatFilter] = useState("all");
  const isChat = active.key === "chat";
  const filterOptions = isChat ? CHAT_FILTER_OPTIONS : FILTER_OPTIONS;
  const activeFilter = isChat ? chatFilter : filter;
  const setActiveFilter = isChat ? setChatFilter : setFilter;

  const onKeyDown = (e) => {
    const i = CONTROL_PANELS.findIndex((p) => p.key === selectedKey);
    let next = null;
    if (e.key === "ArrowRight") next = (i + 1) % CONTROL_PANELS.length;
    else if (e.key === "ArrowLeft")
      next = (i - 1 + CONTROL_PANELS.length) % CONTROL_PANELS.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = CONTROL_PANELS.length - 1;
    if (next == null) return;
    e.preventDefault();
    const key = CONTROL_PANELS[next].key;
    setActiveKey(key);
    document.getElementById(`control-tab-${key}`)?.focus();
  };

  return (
    <section
      className={`bg-white rounded-2xl border border-gray-200/70 overflow-hidden flex flex-col ${fill ? "lg:absolute lg:inset-0" : ""} ${className}`}
    >
      {/* Tab row: equal-width segmented tabs. Theme: selected = dark gradient pill with white
          text (like the top bar's Change Stream button); others = gray text, light hover. */}
      <div
        role="tablist"
        aria-label="Session controls"
        onKeyDown={onKeyDown}
        className="flex gap-1 p-1.5 bg-gray-50 border-b border-gray-200"
      >
        {CONTROL_PANELS.map(({ key, title }) => {
          const selected = key === selectedKey;
          const s = stats[key];
          return (
            <button
              key={key}
              id={`control-tab-${key}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`control-panel-${key}`}
              tabIndex={selected ? 0 : -1}
              title={s.summary}
              onClick={() => setActiveKey(key)}
              className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-xs font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-1 ${selected ? "bg-gradient-to-r from-gray-800 to-black text-white shadow-sm" : "text-gray-600 hover:text-gray-900 hover:bg-white"}`}
            >
              <span className="truncate">{title}</span>
              <TabBadge badge={s.badge} />
            </button>
          );
        })}
      </div>

      {/* Summary line + list filter for the open tab (All / Active / Inactive, or the chat set).
          The filter is a plain text tab bar: the selected one is dark with an underline bar
          sitting on the row's bottom border, the rest gray. Native radios underneath (hidden),
          so arrow keys and screen readers work as before. */}
      <div className="px-3 bg-gray-50/70 border-b border-gray-100 flex items-stretch gap-1.5 text-[11px] font-medium text-gray-600">
        {/* Bottom-aligned on the same text line as the filter tabs: their 4px bottom padding +
            2px bar, less the 1px they overlap the row's border = 5px under the text. */}
        <div className="min-w-0 self-end flex items-center gap-1.5 pt-2 pb-[5px]">
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${stats[active.key].live ? "bg-emerald-500" : "bg-gray-300"}`}
            aria-hidden="true"
          />
          <span className="truncate leading-none">{stats[active.key].summary}</span>
        </div>
        <div
          role="radiogroup"
          aria-label="Filter items"
          className="ml-auto flex items-stretch gap-3 shrink-0"
        >
          {filterOptions.map((opt) => {
            const checked = activeFilter === opt.value;
            return (
              <label
                key={opt.value}
                className={`-mb-px inline-flex items-end border-b-2 px-0.5 pt-2 pb-1 text-[11px] font-medium leading-none select-none transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-gray-900 has-[:focus-visible]:rounded-sm ${
                  checked ? "border-gray-900 text-gray-900" : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
                title={`Show ${opt.label.toLowerCase()} ${isChat ? "messages" : "items"}`}
              >
                <input
                  type="radio"
                  name="control-panel-filter"
                  value={opt.value}
                  checked={checked}
                  onChange={() => setActiveFilter(opt.value)}
                  className="sr-only"
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      </div>

      {/* Panel: full column width. On lg+ it scrolls inside itself; once it hits the top or
          bottom the wheel carries on to the page (no overscroll-contain). Below lg it grows
          to its full height so the page itself scrolls. */}
      <div
        id={`control-panel-${active.key}`}
        role="tabpanel"
        aria-labelledby={`control-tab-${active.key}`}
        className={`p-3 lg:overflow-y-auto [scrollbar-width:thin] [scrollbar-color:var(--color-gray-300)_transparent] ${fill ? "lg:flex-1 lg:min-h-0" : "lg:max-h-[60vh]"}`}
      >
        <ActiveBody key={active.key} embedded filter={activeFilter} fill={fill} />
      </div>
    </section>
  );
}

// The sticky toast (setStickyToast) shown while live but the stream isn't playing.
const STREAM_NOT_PLAYING_TOAST = "stream-not-playing";

// Single-page view for /admin/sessiondetails/:id/dashboard (no tab switching):
//   AdminLayout top section (top bar + header)
//   -> 3 columns on lg+: Live Stream + Stream Health (15%) | Zoom host panel (55%) | control tabs (30%)
//   -> Dashboard
// While a quiz presentation runs (ctx.presMode), the same row re-lays out to 2 columns:
// the left cards move underneath the player, the player keeps its compact 55% size, and
// the right column swaps the control tabs for the fixed-height presentation itself.
// Closing the presentation reverts this automatically.
export default function MainPage() {
  const { id } = useParams();
  const { ctx, loading, session, userDetailsFailed, userNotVerified } = useSessionState(id, "dashboard");
  const quizPresenting = Boolean(ctx?.presMode);
  // Selected control tab lives here so exiting a quiz presentation returns to the
  // same tab (usually Quiz) the admin started it from, instead of resetting.
  const [controlTab, setControlTab] = useState(CONTROL_PANELS[0].key);
  // "Others" modal: the control tabs are swapped for the presentation while it runs,
  // so this button is the only way back to Poll/Q&A/Chat during a presentation.
  const [othersOpen, setOthersOpen] = useState(false);
  useEffect(() => {
    if (!quizPresenting) setOthersOpen(false);
  }, [quizPresenting]);
  // What the cards play: a live stratcreative overlay wins, otherwise the streaming
  // url from the cme-common payload (session.data.streaming).
  const creativeSrc = useCreativeSrc();
  const streamSrc = streamUrl(session);
  // Mux live-stream telemetry (session.data.health), published on cme-common.
  const streamHealth = session?.data?.health ?? null;
  // Whether the stream url is actually serving advancing video - the shared playing status
  // (one monitor per url), which Go Live's own check reads and updates too.
  // A "Web Page" stream (a link the admin typed in) is checked as a plain link instead:
  // can a player open it (useLinkPlayable) - it has no live HLS timeline to watch.
  const isWebPageStream = isWebPageVendor(session?.data?.streaming?.vendor);
  const hlsPlayback = useStreamPlayback(isWebPageStream ? null : streamSrc);
  const linkPlayback = useLinkPlayable(isWebPageStream ? streamSrc : null);
  const playback = isWebPageStream ? linkPlayback : hlsPlayback;
  // Live for viewers (streamToUser) while the Stream Health card's Playing says No: a toast
  // that can't be closed says so (with the card's reason) until the stream plays again or
  // Live is stopped. "Checking" isn't a No - nothing shows until a check has failed.
  const setStickyToast = ctx?.setStickyToast;
  const isStreamLive = Boolean(session?.data?.streamToUser);
  const notPlayingReason =
    isStreamLive && streamSrc && playback?.playing === false
      ? (isWebPageStream ? linkCheckReasonText : playbackReasonText)(playback.reason)
      : null;
  const notPlayingMsg =
    notPlayingReason === null
      ? null
      : `The stream isn't playing for viewers${notPlayingReason ? ` (${notPlayingReason})` : ""}. Please check the stream.`;
  useEffect(() => {
    if (!setStickyToast) return undefined;
    setStickyToast(STREAM_NOT_PLAYING_TOAST, notPlayingMsg);
    return () => setStickyToast(STREAM_NOT_PLAYING_TOAST, null);
  }, [notPlayingMsg, setStickyToast]);
  // lg+ = 3-column layout; below it the page stacks and gets the one-line status rows.
  const isDesktop = useMediaQuery("(min-width: 1024px)", true);
  // Browser tab = the session's title + small brand logo (cme-common detail.title and
  // setting.branding.smlogo). Set here, outside the gate, so the status screens carry it too.
  useDocumentTitle(session?.data?.detail?.title);
  useFavicon(session?.data?.setting?.branding?.smlogo);
  // Internet lost / back: a toast that stays while offline, a green one when it returns.
  // Outside the gate, like the toasts themselves, so every admin screen gets them.
  const online = useOnlineStatus();
  useConnectionToasts(online, ctx?.showMsg, ctx?.setStickyToast);

  return (
    <>
      {/* Outside the gate: Ably runs on every admin screen (live dashboard or status screen). */}
      <RealtimeDownModal />
      {/* showMsg's toast alerts, bottom-right */}
      <Toaster toasts={ctx?.toasts ?? []} stickyToasts={ctx?.stickyToasts ?? []} onDismiss={ctx?.dismissToast} />
      <SessionGate loading={loading} session={session} userDetailsFailed={userDetailsFailed} userNotVerified={userNotVerified}>
        {() => (
          <SessionCtx.Provider value={ctx}>
            <AdminLayout>
              {/* Below lg (stacked): stream health as a one-line row on top, then video,
                  control tabs, creative video + Section (order-last), then the Dashboard. */}
              {!isDesktop && (
                <div className="mb-4 flex flex-col gap-2">
                  {/* Below md: streaming steps row, above Stream Health (portaled in by
                      StreamingVendorControls, which owns the step state) */}
                  <div id={MOBILE_STEPS_SLOT_ID} className="md:hidden empty:hidden" />
                  <StreamHealthStrip
                    src={streamSrc}
                    health={streamHealth}
                    playback={playback ?? undefined}
                    webPage={isWebPageStream}
                  />
                </div>
              )}

              {/* 3-column layout on lg+ (>=1024px), left 15% | player 55% | right 30%.
                  Below lg the three sections stack. Each section is a slot - put anything in it.
                  During a quiz presentation: left column empties (its cards move under the
                  player), player stays at 55%, the fixed-height quiz takes the remaining 45%. */}
              <div
                className={`mb-4 lg:mb-6 grid grid-cols-1 gap-4 items-start ${quizPresenting ? "lg:grid-cols-[55fr_45fr]" : "lg:grid-cols-[15fr_55fr_30fr]"}`}
              >
                {/* Left section (15%) - only while no presentation is running.
                    Below lg it moves after the control tabs (order-last). */}
                {!quizPresenting && (
                  <StreamCards
                    src={streamSrc}
                    creativeSrc={creativeSrc}
                    health={streamHealth}
                    playback={playback}
                    webPage={isWebPageStream}
                    className="min-w-0 flex flex-col gap-4 lg:gap-2 order-last lg:order-none"
                  />
                )}

                {/* Center section (55% - unchanged while presenting, so the player never stretches).
                    During a presentation the stream cards sit underneath it. */}
                <div className="min-w-0">
                  <MeetingPanel
                    sessionId={id}
                    meetingNumber={session.data?.hosting?.meeting_id}
                    password={session.data?.hosting?.password}
                    vendor={session.data?.hosting?.vendor}
                    className="lg:min-h-[560px]"
                  />
                  {quizPresenting && (
                    <StreamCards
                      src={streamSrc}
                      creativeSrc={creativeSrc}
                      health={streamHealth}
                      playback={playback}
                      webPage={isWebPageStream}
                      className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3"
                    >
                      {/* "Others" - right after the Section card, same card-button style */}
                      <button
                        type="button"
                        onClick={() => setOthersOpen(true)}
                        className="flex-1 w-full flex items-center gap-2 bg-white rounded-2xl border border-gray-200/70 p-3 text-left hover:shadow-sm hover:border-gray-300 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900"
                      >
                        <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-gray-800 to-black text-white flex items-center justify-center shrink-0">
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
                          </svg>
                        </span>
                        <span className="text-xs font-semibold text-gray-800">
                          Others
                        </span>
                        <svg
                          className="w-4 h-4 ml-auto text-gray-400 shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </StreamCards>
                  )}
                </div>

                {/* Right section (30%, 45% while presenting) - control tabs, or the live quiz presentation.
                    lg+: the control tabs fill this cell, stretched to the row - so they are exactly
                    as tall as the meeting player (absolute, so their own content never grows the
                    row; the panel scrolls inside). 560px floor = the player's own minimum. */}
                <div className={`min-w-0 ${quizPresenting ? "" : "lg:relative lg:self-stretch lg:min-h-[560px]"}`}>
                  {quizPresenting ? (
                    <InteractiveQuiz inline />
                  ) : (
                    <ControlTabs
                      activeKey={controlTab}
                      onActiveKeyChange={setControlTab}
                      fill
                    />
                  )}
                </div>
              </div>

              {/* "Others" modal: the control tabs again while a presentation is running.
                  Shared FullViewModal (portal, Esc, backdrop click to close). */}
              {othersOpen && (
                <FullViewModal
                  labelId="others-modal-title"
                  onClose={() => setOthersOpen(false)}
                  meta={
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Session controls
                    </span>
                  }
                  title={
                    <span className="text-base font-semibold text-gray-900">
                      Control Panels
                    </span>
                  }
                >
                  <ControlTabs
                    activeKey={controlTab}
                    onActiveKeyChange={setControlTab}
                  />
                </FullViewModal>
              )}

              <Dashboard />
            </AdminLayout>
          </SessionCtx.Provider>
        )}
      </SessionGate>
    </>
  );
}
